/**
 * C1a's calls, typed from the generated contract (`KLQ-20260819-005`).
 *
 * FOUR THINGS THE CONTRACT SAYS THAT THIS FILE IS BUILT AROUND
 *
 * 1. **The tree is three lists, not one.** The hierarchy, the slots and every slot's current
 *    version each arrive whole and unpaginated, and the client joins them on `slot_id`. The
 *    current-version listing exists precisely so the tree costs three requests instead of
 *    three plus one per drawing (D1) — `hierarchy` may not import `versions`, so the summary
 *    could not be embedded in the slot row.
 * 2. **Nothing here renders a drawing name.** `next` answers what the next publish will be
 *    called, because `hierarchy/naming.py` is the only implementation of the `{rev}` grammar
 *    anywhere (`MCK-20260521-005`, D2). A helper in this file that built `${identifier}-r${n}`
 *    would be the second one.
 * 3. **`POST /uploads` has two answers.** A ticket to PUT bytes to, or `already_present` —
 *    this workspace already references those exact bytes, so zero travel. They are
 *    discriminated by `status`, and `upload.ts` is where the branch lives.
 * 4. **No endpoint takes a slot identifier.** A drawing's address is rendered from where it
 *    sits in the tree; every call here names ids.
 */

import { api, unwrap } from '@/api/client';
import type { components } from '@/api/generated/openapi';

type Schemas = components['schemas'];

export type HierarchyTree = Schemas['TreeResponse'];
export type HierarchyNode = Schemas['HierarchyNodeResponse'];
export type Slot = Schemas['SlotResponse'];
export type SlotPage = Schemas['Page_SlotResponse_'];
export type CurrentVersion = Schemas['CurrentVersionResponse'];
/** A slot's derived deliverable status (`KLQ-20260824-001`) — planned, in progress, done. */
export type DeliverableStatus = Slot['status'];
/**
 * The task a drawing is a deliverable of, as the server chose to name it
 * (`KLQ-20260824-001`). Present only when this caller may open the task behind it — the
 * audience and zone checks are applied server-side, so a row that has one may link to it.
 */
export type OwningTask = NonNullable<Slot['owning_task']>;
export type CurrentVersionPage = Schemas['Page_CurrentVersionResponse_'];
export type DrawingSummary = Schemas['DrawingSummaryResponse'];
export type NextVersion = Schemas['NextVersionResponse'];
export type Version = Schemas['VersionResponse'];
export type VersionPage = Schemas['Page_VersionResponse_'];
export type VersionFile = Schemas['VersionFileResponse'];
export type FileProposal = Schemas['FileProposalResponse'];
export type PublishFromFilesItem = Schemas['PublishFromFilesItem'];
export type PublishedFromFile = Schemas['PublishedFromFileResponse'];
export type HierarchyPreset = Schemas['HierarchyPresetResponse'];
export type HierarchyPresetPage = Schemas['Page_HierarchyPresetResponse_'];
export type NumberingScheme = Schemas['NumberingSchemeResponse'];
export type DocumentType = Schemas['DocumentTypeResponse'];
export type DocumentTypePage = Schemas['Page_DocumentTypeResponse_'];
/** The phase → document-type mapping for one project (`KLQ-20260827-003`). */
export type DocumentApplicability = Schemas['DocumentApplicabilityResponse'];
/** The project's *who drew this* vocabulary (`MCK-20260812-011`). */
export type DisciplinePage = Schemas['Page_DisciplineResponse_'];
export type Discipline = Schemas['DisciplineResponse'];
export type DocumentStanding = Schemas['DocumentStandingResponse'];
export type NumberingSchemePage = Schemas['Page_NumberingSchemeResponse_'];
export type LevelPlan = Schemas['LevelPlanRequest'];
export type GenerationPlan = Schemas['GenerationPlanResponse'];
export type PlannedNode = Schemas['PlannedNodeResponse'];
export type HierarchyGroup = Schemas['HierarchyGroupResponse'];
export type NodeMove = Schemas['NodeMoveRequest'];
export type ReorderResult = Schemas['ReorderResponse'];
export type UploadTicket = Schemas['UploadTicketResponse'];
export type UploadAlreadyPresent = Schemas['UploadAlreadyPresentResponse'];
export type UploadState = Schemas['UploadStateResponse'];
export type UploadKind = Schemas['UploadRequest']['kind'];
export type DownloadUrl = Schemas['DownloadUrlResponse'];

/**
 * The container kinds a person may add, in the order they nest (`hierarchy/models.py`).
 *
 * TWIN: `CONTAINER_KINDS` there. Three of its members are deliberately missing here:
 * `project` is the root the seed creates and nobody adds a second one, `unfiled` is the
 * system bucket orphaned drawings land in rather than a level anybody chooses, and `slot` is
 * not a container at all — a drawing is created through `POST /slots`, which also mints its
 * address.
 */
export const CONTAINER_KINDS = [
  'building',
  'floor',
  'zone',
  'department',
  'room',
  'house',
  'row',
  'unit',
  'site',
  'street',
  'stage',
  // Part E — prílohy. A place in the tree for a document that fits no object, no discipline
  // and no phase (`KLQ-20260827-003` §0), offered like any other level.
  'appendix',
] as const;
export type ContainerKind = (typeof CONTAINER_KINDS)[number];

// --- The tree ---------------------------------------------------------------

export function readHierarchy(projectId: string): Promise<HierarchyTree> {
  return unwrap(
    api.GET('/api/v1/projects/{project_id}/hierarchy', {
      params: { path: { project_id: projectId } },
    }),
  );
}

/**
 * Which part of the tree a listing is about (`KLQ-20260825-008` §5.1).
 *
 * `under` is the slicer every list endpoint here takes — *answer about these subtrees* — and
 * `depth` counts levels from each of them, `1` being the roots themselves. A drawing is a
 * leaf, so the only bound a screen actually sends is `{ under: [nodeId], depth: 2 }`: **the
 * drawings filed directly on this level**, which is the question a row being expanded asks.
 *
 * Nothing at all is still the whole project, which is what the importer and the slicer rail
 * legitimately want.
 */
export interface Branch {
  readonly under?: readonly string[];
  readonly depth?: number;
  /**
   * How many pages of this branch to walk. Absent is one.
   *
   * **Absent used to mean *"whatever the server's default is"*, which was 50** — so a floor
   * with eighty drawings drew fifty of them under a header that said eighty, and said nothing
   * about the thirty. The header's number comes from `drawing_count` on the shape and was
   * right; the rows were the ones lying. Found 2026-09-10, an hour after the tasks plan got
   * the same treatment and this screen did not.
   */
  readonly pages?: number;
  /**
   * The register's three axes (`MCK-20260812-011`), as the rail narrows them. Repeated means
   * *any of these* and the three AND together — the server's rule, not restated here.
   *
   * They ride on the **same** request as the rows they narrow, which is why they are part of a
   * branch rather than a second parameter beside it: the version pills are fetched by the same
   * shape, and a pill fetched under a different question would be drawn on a row that is not
   * there.
   */
  readonly object?: readonly string[];
  readonly discipline?: readonly string[];
  readonly documentType?: readonly string[];
}

/** An absent field and an empty one ask the same nothing, and neither is sent. */
function asked(
  key: string,
  values: readonly string[] | undefined,
): Record<string, string[]> {
  return values === undefined || values.length === 0 ? {} : { [key]: [...values] };
}

function branchQuery(
  branch: Branch,
  cursor: string | null = null,
): Record<string, string[] | number | string> {
  return {
    ...asked("under", branch.under),
    ...(branch.depth === undefined ? {} : { depth: branch.depth }),
    ...asked("object_id", branch.object),
    ...asked("discipline_id", branch.discipline),
    ...asked("document_type_id", branch.documentType),
    // Only when the branch is bounded. A whole-project fetch — the importer, the rail — is not
    // paged here and never was; giving it a limit would change what those callers get.
    ...(branch.depth === undefined ? {} : { limit: BRANCH_PAGE }),
    ...(cursor === null ? {} : { cursor }),
  };
}

/**
 * How many of one level's drawings arrive per branch page.
 *
 * `MAX_LIMIT` on the server is 200 and it **clamps rather than refuses**, so asking for more in
 * one request buys nothing — which is why a long branch walks the cursor instead of widening.
 *
 * TWIN: `MAX_LIMIT` in `backend/src/kalqa/core/pagination.py`, and `BRANCH_PAGE` in
 * `features/tasks/api.ts`, which is the same number for the same reason on the other hierarchy.
 */
export const BRANCH_PAGE = 200;

/**
 * Walk a bounded branch, `pages` deep, and hand back one page-shaped answer.
 *
 * The walk is here rather than in an infinite query for `features/tasks`' reason: how many
 * branches are open is not knowable at compile time, `useQueries` takes plain options, and
 * hooks may not run in a loop. So the page count is part of the cache key and raising it
 * re-walks from the first page — one extra page's worth on the rare level that has more than
 * `BRANCH_PAGE` drawings on it.
 */
async function walk<Item>(
  fetchPage: (cursor: string | null) => Promise<{
    items: Item[];
    has_more: boolean;
    next_cursor?: string | null;
  }>,
  pages: number,
): Promise<{ items: Item[]; has_more: boolean; next_cursor: string | null }> {
  const items: Item[] = [];
  let cursor: string | null = null;
  let hasMore = false;

  for (let page = 0; page < pages; page += 1) {
    const answer = await fetchPage(cursor);
    items.push(...answer.items);
    hasMore = answer.has_more;
    cursor = answer.next_cursor ?? null;
    if (!hasMore || cursor === null) break;
  }
  return { items, has_more: hasMore, next_cursor: cursor };
}

/** The project's drawings, or the drawings on one level of it. See `Branch`. */
export function listSlots(projectId: string, branch: Branch = {}): Promise<SlotPage> {
  return walk(
    (cursor) =>
      unwrap(
        api.GET('/api/v1/projects/{project_id}/slots', {
          params: {
            path: { project_id: projectId },
            query: branchQuery(branch, cursor),
          },
        }),
      ),
    branch.pages ?? 1,
  );
}

/**
 * The current version of every drawing in the same branch, in one request — D1. See the
 * module note.
 *
 * Takes `Branch` as of 2026-09-10 for the reason its neighbour does: the pill is drawn *on* a
 * slot row, so it has to arrive with the same branch the slot did — fetching the two under
 * different bounds would mean drawing a pill for a row that is not there. Without it this was
 * the one project-wide read left on a screen that had stopped making any others.
 */
export function listCurrentVersions(
  projectId: string,
  branch: Branch = {},
): Promise<CurrentVersionPage> {
  // The pills are fetched by the same shape as the rows they sit on, so they walk with them:
  // a branch whose second page of drawings arrived without its second page of pills would draw
  // "no version" on rows that have one.
  return walk(
    (cursor) =>
      unwrap(
        api.GET('/api/v1/projects/{project_id}/current-versions', {
          params: {
            path: { project_id: projectId },
            query: branchQuery(branch, cursor),
          },
        }),
      ),
    branch.pages ?? 1,
  );
}

/**
 * The overview's three figures about the drawings — a count, the week's publications, the six
 * newest — from bounded statements on the server. Until 2026-09-25 the overview fetched every
 * slot and every current version of the project to compute them client-side
 * (`docs/2026-09-25-functional-performance-review.md` §2.2, F2).
 */
export function readDrawingSummary(projectId: string): Promise<DrawingSummary> {
  return unwrap(
    api.GET('/api/v1/projects/{project_id}/drawing-summary', {
      params: { path: { project_id: projectId } },
    }),
  );
}

export function listPresets(): Promise<HierarchyPresetPage> {
  return unwrap(api.GET('/api/v1/hierarchy-presets', {}));
}

/** Replace an empty project's hierarchy with a preset's levels. */
export function seedHierarchy(projectId: string, preset: string): Promise<HierarchyTree> {
  return unwrap(
    api.PUT('/api/v1/projects/{project_id}/hierarchy', {
      params: { path: { project_id: projectId } },
      body: { preset },
    }),
  );
}

/**
 * How generated levels are named — reference data, like the presets.
 *
 * The samples come from the server rather than being rendered here: `schemes.py` is the only
 * implementation of `1.PP, 1.NP, 2.NP`, and a picker that previewed its own version of that
 * rule would be the second one — the same discipline that keeps version names out of this
 * file.
 */
export function listSchemes(): Promise<NumberingSchemePage> {
  return unwrap(api.GET('/api/v1/numbering-schemes', {}));
}

/**
 * What a drawing can *be* in this project — pôdorys, rez, vizualizácia.
 *
 * Project vocabulary (`MCK-20260812-011`), seeded at project creation from the workspace's
 * norm preset. Reference data: one fetch per project session, shared rather than re-requested
 * per surface. The generator turns a selection of these into one empty drawing per level.
 */
/**
 * What each phase says about each document type (`KLQ-20260827-003`).
 *
 * Every phase in one response, deliberately: switching phase in the picker must not produce a
 * request. Only what differs from `allowed` is listed — absence *is* the default, and the
 * server names that default in the body so no client hardcodes it.
 */
export function readDocumentApplicability(projectId: string): Promise<DocumentApplicability> {
  return unwrap(
    api.GET('/api/v1/projects/{project_id}/document-applicability', {
      params: { path: { project_id: projectId } },
    }),
  );
}

/**
 * Who draws in this project. Reference data, fetched once and shared.
 *
 * Both codes travel: `statutory_code` is what a file name renders, `practice_code` is what the
 * studio calls the folder. Which one is *shown* is a display choice; which one reaches a name
 * is not the client's decision, because the client never builds a name.
 */
export function listDisciplines(projectId: string): Promise<DisciplinePage> {
  return unwrap(
    api.GET('/api/v1/projects/{project_id}/disciplines', {
      params: { path: { project_id: projectId } },
    }),
  );
}

export function listDocumentTypes(projectId: string): Promise<DocumentTypePage> {
  return unwrap(
    api.GET('/api/v1/projects/{project_id}/document-types', {
      params: { path: { project_id: projectId } },
    }),
  );
}

/**
 * Generate levels from counts (`KLQ-20260820-004`).
 *
 * `dry_run: true` writes nothing and returns the tree that would be created plus a sample
 * drawing address. That preview is not optional politeness: a generated code goes into every
 * identifier beneath it and locks on the first published version.
 */
export function generateHierarchy(
  projectId: string,
  body: {
    readonly parent_id: string | null;
    readonly levels: readonly LevelPlan[];
    readonly dry_run: boolean;
  },
): Promise<GenerationPlan> {
  return unwrap(
    api.POST('/api/v1/projects/{project_id}/hierarchy-plans', {
      params: { path: { project_id: projectId } },
      body: { ...body, levels: [...body.levels] },
    }),
  );
}

/** Copy a level and its container descendants, N times. Never a drawing, never a scope grant. */
export function duplicateNode(
  projectId: string,
  body: {
    readonly source_node_id: string;
    readonly count: number;
    readonly scheme: string;
    readonly dry_run: boolean;
  },
): Promise<GenerationPlan> {
  return unwrap(
    api.POST('/api/v1/projects/{project_id}/hierarchy-copies', {
      params: { path: { project_id: projectId } },
      body,
    }),
  );
}

/**
 * Put a new level **above** levels that already exist.
 *
 * The third direction: `hierarchy-plans` writes downwards, `hierarchy-copies` sideways, this
 * upwards. `dry_run` reports how many drawing addresses would change, because a level above a
 * drawing is part of its address.
 */
export function groupNodes(
  projectId: string,
  body: {
    readonly node_ids: readonly string[];
    readonly kind: ContainerKind;
    readonly name: string;
    readonly dry_run: boolean;
  },
): Promise<HierarchyGroup> {
  return unwrap(
    api.POST('/api/v1/projects/{project_id}/hierarchy-groups', {
      params: { path: { project_id: projectId } },
      body: { ...body, node_ids: [...body.node_ids] },
    }),
  );
}

/**
 * Move levels — the fourth direction, and the one nothing called until `KLQ-20260825-007`.
 *
 * **One drag, whole.** The request is a list because a rearrangement is one decision: every
 * move is validated against the tree *as it will be* — including the cycle check, which is the
 * one invariant the database cannot express — and then all of them are written in one
 * transaction, or none is. A client that sent them one at a time would be able to leave a tree
 * in a shape nobody chose, and one of those shapes is a cycle.
 *
 * **`parent_id` is not nullable here**, unlike everywhere else in this file: only the project
 * root may sit at the top of a tree, and it is the one node that cannot be moved. Moving a
 * level "to the top" means moving it under the root, by id.
 *
 * **There is no `dry_run`.** Every other structural call in this file previews first, and this
 * one cannot — see `ProjectShapeEditor`, which is why the count it states before a move is an
 * upper bound rather than the server's own number. Giving this endpoint the preview its
 * siblings have is a backend spec, filed with `parent_ids` on `GenerationRequest`.
 */
export function reorderHierarchy(
  projectId: string,
  moves: readonly NodeMove[],
): Promise<ReorderResult> {
  return unwrap(
    api.PATCH('/api/v1/projects/{project_id}/hierarchy', {
      params: { path: { project_id: projectId } },
      body: { moves: [...moves] },
    }),
  );
}

export function createNode(
  projectId: string,
  body: { readonly parent_id: string | null; readonly kind: ContainerKind; readonly name: string },
): Promise<HierarchyNode> {
  return unwrap(
    api.POST('/api/v1/projects/{project_id}/hierarchy', {
      params: { path: { project_id: projectId } },
      body,
    }),
  );
}

export function renameNode(
  projectId: string,
  nodeId: string,
  name: string,
): Promise<HierarchyNode> {
  return unwrap(
    api.PATCH('/api/v1/projects/{project_id}/hierarchy/{node_id}', {
      params: { path: { project_id: projectId, node_id: nodeId } },
      body: { name },
    }),
  );
}

/**
 * Who is responsible for this level (`KLQ-20260828-003`).
 *
 * **`null` is sent explicitly, and that is the point.** The endpoint reads `model_fields_set`
 * to tell *"leave it"* from *"clear it"*, so a body that omitted the key would leave the level's
 * architect in place — which is the opposite of what clearing the cell means. Every other field
 * is absent here for the same reason in reverse: this call must not touch the name or the code.
 */
export function setNodeResponsible(
  projectId: string,
  nodeId: string,
  userId: string | null,
): Promise<HierarchyNode> {
  return unwrap(
    api.PATCH('/api/v1/projects/{project_id}/hierarchy/{node_id}', {
      params: { path: { project_id: projectId, node_id: nodeId } },
      body: { responsible_user_id: userId },
    }),
  );
}

/**
 * Remove a container.
 *
 * It never silently deletes drawings — they land in the `unfiled` bucket. A member whose only
 * zones lie inside the removed branch **loses the project** rather than being widened to all
 * of it (`KLQ-20261028-008`); the old `409` naming them is gone. `grant_full_access_to`
 * (widen instead) is an unrestricted caller's act and this client does not send it.
 */
export function removeNode(projectId: string, nodeId: string): Promise<unknown> {
  return unwrap(
    api.DELETE('/api/v1/projects/{project_id}/hierarchy/{node_id}', {
      params: { path: { project_id: projectId, node_id: nodeId } },
    }),
  );
}

/** Add a drawing. The server mints its identifier from where it sits; nobody types one. */
export function createSlot(
  projectId: string,
  body: {
    readonly parent_id: string | null;
    readonly name: string;
    readonly drawing_number?: string;
    readonly title?: string;
  },
): Promise<Slot> {
  return unwrap(
    api.POST('/api/v1/projects/{project_id}/slots', {
      params: { path: { project_id: projectId } },
      body,
    }),
  );
}

/**
 * One drawing — **the only response that carries `former_identifiers`**
 * (`MCK-20260812-011` §Naming).
 *
 * The tree already hands the detail pane every other field of this row, so this is not a
 * second source for what is on screen; it is the one fact a list cannot afford to carry,
 * because filling it on `GET /slots` would be a query per row. Read once when the pane opens
 * and cached like the version list beside it — never per tree row.
 */
export function readSlot(projectId: string, slotId: string): Promise<Slot> {
  return unwrap(
    api.GET('/api/v1/projects/{project_id}/slots/{slot_id}', {
      params: { path: { project_id: projectId, slot_id: slotId } },
    }),
  );
}

/** One drawing's editable fields. The identifier is not among them — the tree mints it. */
export function updateSlot(
  projectId: string,
  slotId: string,
  body: SlotPatch,
): Promise<Slot> {
  return unwrap(
    api.PATCH('/api/v1/projects/{project_id}/slots/{slot_id}', {
      params: { path: { project_id: projectId, slot_id: slotId } },
      body,
    }),
  );
}

export type SlotPatch = Schemas['SlotUpdateRequest'];
export type SlotBulkEdit = Schemas['SlotBulkEditRequest'];
export type SlotBulkEditResult = Schemas['SlotBulkEditResponse'];

/**
 * One value over a selection of drawings — what the grid's object column writes through.
 *
 * `200` even when part of the selection missed: `skipped` names the rows, which is what lets a
 * fan-out over eighty drawings survive one that somebody deleted while the register was open.
 */
export function bulkEditSlots(
  projectId: string,
  body: SlotBulkEdit,
): Promise<SlotBulkEditResult> {
  return unwrap(
    api.PATCH('/api/v1/projects/{project_id}/slots', {
      params: { path: { project_id: projectId } },
      body,
    }),
  );
}

// --- Versions --------------------------------------------------------------

/** One slot's history, newest first. Unpaginated by contract. */
export function listVersions(projectId: string, slotId: string): Promise<VersionPage> {
  return unwrap(
    api.GET('/api/v1/projects/{project_id}/versions', {
      params: { path: { project_id: projectId }, query: { slot_id: slotId } },
    }),
  );
}

/** What the next publish onto this slot will be called — D2. Computes, reserves nothing. */
export function readNextVersion(projectId: string, slotId: string): Promise<NextVersion> {
  return unwrap(
    api.GET('/api/v1/projects/{project_id}/next-version', {
      params: { path: { project_id: projectId }, query: { slot_id: slotId } },
    }),
  );
}

export interface PublishBody {
  readonly slot_id: string;
  readonly rendition_upload_id: string;
  readonly source_upload_ids: readonly string[];
  readonly version_note: string;
}

/**
 * Publish a version. `Idempotency-Key` is accepted, not demanded — sent here because a
 * double-clicked Publish would otherwise mint two versions off two valid uploads, which no
 * unique constraint prevents (`versions/router.py:publish_version`).
 */
export function publishVersion(
  projectId: string,
  body: PublishBody,
  idempotencyKey: string,
): Promise<Version> {
  return unwrap(
    api.POST('/api/v1/projects/{project_id}/versions', {
      params: { path: { project_id: projectId } },
      headers: { 'Idempotency-Key': idempotencyKey },
      body: { ...body, source_upload_ids: [...body.source_upload_ids] },
    }),
  );
}

/**
 * What publishing these files would do — one proposal per name, in the order sent
 * (`KLQ-20261023-010`). A read: the match rule lives next to the naming template on the
 * server, so the sheet renders a proposal and never re-derives one.
 */
export function proposeFromFiles(
  projectId: string,
  filenames: readonly string[],
): Promise<readonly FileProposal[]> {
  return unwrap(
    api.POST('/api/v1/projects/{project_id}/file-proposals', {
      params: { path: { project_id: projectId } },
      body: { filenames: [...filenames] },
    }),
  ).then((page) => page.items);
}

/**
 * Publish a batch: each item onto its drawing, or onto a drawing the same request creates.
 * One transaction server-side, so a failed item leaves no empty slot behind. `Idempotency-Key`
 * as on `publishVersion`, for the same double-click.
 */
export function publishFromFiles(
  projectId: string,
  items: readonly PublishFromFilesItem[],
  idempotencyKey: string,
): Promise<readonly PublishedFromFile[]> {
  return unwrap(
    api.POST('/api/v1/projects/{project_id}/publish-from-files', {
      params: { path: { project_id: projectId } },
      headers: { 'Idempotency-Key': idempotencyKey },
      body: { items: [...items] },
    }),
  ).then((page) => page.items);
}

/** Move the slot's current pointer back to the version this one superseded. Audit-logged. */
export function rollbackVersion(versionId: string, idempotencyKey: string): Promise<Version> {
  return unwrap(
    api.POST('/api/v1/versions/{version_id}/rollback', {
      params: { path: { version_id: versionId } },
      headers: { 'Idempotency-Key': idempotencyKey },
    }),
  );
}

/**
 * Correct the phase snapshot on a published version. Audit-logged server-side
 * (`versions.service.correct_phase`); the client writes no record of its own.
 */
export function correctVersionPhase(
  versionId: string,
  phaseId: string | null,
): Promise<Version> {
  return unwrap(
    api.PATCH('/api/v1/versions/{version_id}/phase', {
      params: { path: { version_id: versionId } },
      body: { phase_id: phaseId },
    }),
  );
}

// --- Files ------------------------------------------------------------------

export function readDownloadUrl(blobRefId: string): Promise<DownloadUrl> {
  return unwrap(
    api.GET('/api/v1/files/{blob_ref_id}/download-url', {
      params: { path: { blob_ref_id: blobRefId } },
    }),
  );
}

/**
 * Ask for the file's tile pyramid.
 *
 * Fired after a publish and **never awaited for its answer** (`KLQ-20260819-005`, "Warming
 * the viewer"): this wave cannot display a pyramid, C1b can, and `202` plus uniqueness on
 * `(workspace, blob, kind)` means a repeat starts no second build. A failure here must not
 * touch anything the person just did — the version is published either way.
 */
export function requestRendition(blobRefId: string): Promise<unknown> {
  return unwrap(
    api.POST('/api/v1/files/{blob_ref_id}/rendition', {
      params: { path: { blob_ref_id: blobRefId } },
    }),
  );
}

// --- Uploads ----------------------------------------------------------------

export interface UploadRequestBody {
  readonly filename: string;
  readonly mime_type: string;
  readonly byte_size: number;
  readonly kind: UploadKind;
  readonly content_sha256: string;
}

/**
 * Either a ticket to PUT to, or the short-circuit. Discriminated by `status`.
 *
 * `Idempotency-Key` is **demanded** here, not optional (`files/router.py:request_upload`):
 * without it a replayed request mints a second staging object nobody ever attaches, or a
 * second phantom `ready` upload in the `already_present` case. The key belongs to one chosen
 * file — the caller mints it when the file is picked and keeps it, so a retry of that same
 * file replays the first answer instead of starting a second upload.
 */
export function requestUpload(
  body: UploadRequestBody,
  idempotencyKey: string,
): Promise<UploadTicket | UploadAlreadyPresent> {
  return unwrap(
    api.POST('/api/v1/uploads', {
      headers: { 'Idempotency-Key': idempotencyKey },
      body,
    }),
  );
}

export function confirmUpload(uploadId: string): Promise<UploadState> {
  return unwrap(
    api.POST('/api/v1/uploads/{upload_id}/confirm', {
      params: { path: { upload_id: uploadId } },
    }),
  );
}

export function readUploadState(uploadId: string): Promise<UploadState> {
  return unwrap(
    api.GET('/api/v1/uploads/{upload_id}', {
      params: { path: { upload_id: uploadId } },
    }),
  );
}
